import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";
import { ICustomer } from "./customersReducer";
import { IDriver } from "./driversReducer";
import { IEnumPayment } from "./paymentsReducer";
import { IRentalItem } from './rentalItemsReducer'
import { ISite } from "./sitesReducer";
import { tryConfirmLogin } from "./authReducer";

export interface IRentalTimeFrameExtension {
    _id: string;
    daysCount: number;
    payments: IEnumPayment[];
    cost: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

export interface IRentalPackage {
    _id: string;
    rentalItems: IRentalItem[];
    rentalDaysCount: number;
    timeFrameExtensions: IRentalTimeFrameExtension[];
    transportationFee: number;
    totalCost: number;
    status: string;
    deliveryDriver: IDriver | null;
    deliveryTimeStamp: number | null;
    retrievalDriver: IDriver | null;
    retrievalTimeStamp: number | null;
    createAt: string;
    updatedAt: string;
}

export interface IEnumRentalPackage {
    _id: string;
    rentalItems: IRentalItem[];
    totalCost: number;
    rentalDaysCount: number;
    deliveryTimeStamp: number;
}

export interface IDeliveryNote {
    _id: string;
    orderId: string;
    orderRemarks?: string;
    customer: ICustomer;
    site: ISite;
    rentalPackage: IRentalPackage;
    customerSign: string;
    customerComment?: string;
    remarks?: string;
    driverSign: string;
    createdAt: string;
}

class ConfirmDeliveryErrs {
    form: string | null = null;
    orderId: string | null = null;
    customerSign: string | null = null;
    driverSign: string | null = null;
}

class ExtendTimeFrameErrs {
    form: string | null = null;
    daysCount: string | null = null;
    paymentTypes: string | null = null;
    bankName: string | null = null;
    accountName: string | null = null;
    accountNumber: string | null = null;
}

export interface IRentalPackagesState {
    confirmingDelivery: boolean;
    confirmDeliveryErrs: ConfirmDeliveryErrs;
    deliveryConfirmed: boolean;
    fetchingDeliveryNote: boolean;
    deliveryNote: IDeliveryNote | null;
    fetchDeliveryNoteErr: string | null;
    extendingTimeFrame: boolean;
    extendTimeFrameErrs: ExtendTimeFrameErrs;
    extendedTimeFrame: boolean;
    timeExtApproved: boolean;
    approvingTimeExt: boolean;
    approveTimeExtErr: string | null;
}

const initialState = {
    deliveryConfirmed: false,
    confirmingDelivery: false,
    confirmDeliveryErrs: new ConfirmDeliveryErrs(),
    fetchingDeliveryNote: false,
    fetchDeliveryNoteErr: null,
    deliveryNote: null,
    extendingTimeFrame: false,
    extendTimeFrameErrs: new ExtendTimeFrameErrs(),
    extendedTimeFrame: false,
    timeExtApproved: false,
    approvingTimeExt: false,
    approveTimeExtErr: null,
} as IRentalPackagesState;

const rentalPackagesSlice = createSlice({
    name: 'rentalPackages',
    initialState,
    reducers: {
        clearConfirmDeliveryFieldErr: (state: IRentalPackagesState, action: PayloadAction<string>) => {
            state.confirmDeliveryErrs[action.payload as keyof ConfirmDeliveryErrs] = null;
        },
        clearConfirmDeliveryErrs: (state: IRentalPackagesState) => {
            state.confirmDeliveryErrs = new ConfirmDeliveryErrs();
        },
        tryResetConfirmDeliveryFormErr: (state: IRentalPackagesState) => {
            let shouldReset = true;

            for (let key in state.confirmDeliveryErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.confirmDeliveryErrs[key as keyof ConfirmDeliveryErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.confirmDeliveryErrs.form = null;
            }
        },
        confirmDeliveryStart: (state: IRentalPackagesState) => {
            state.confirmingDelivery = true;
            state.deliveryConfirmed = false;
            state.confirmDeliveryErrs = new ConfirmDeliveryErrs();
        },

        confirmDeliveryPass: (state: IRentalPackagesState) => {
            state.confirmingDelivery = false;
            state.deliveryConfirmed = true;
        },
        resetDeliveryConfirmed: (state: IRentalPackagesState) => {
            state.deliveryConfirmed = false;
        },
        confirmDeliveryFail: (state: IRentalPackagesState, action: PayloadAction<IFieldError[]>) => {
            state.confirmingDelivery = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.confirmDeliveryErrs) {
                    if (err.field === key) {
                        state.confirmDeliveryErrs[key as keyof ConfirmDeliveryErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.confirmDeliveryErrs.form) {
                state.confirmDeliveryErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchDeliveryNoteErr: (state: IRentalPackagesState) => {
            state.fetchDeliveryNoteErr = null;
        },
        fetchDeliveryNoteStart: (state: IRentalPackagesState) => {
            state.fetchingDeliveryNote = true;
            state.fetchDeliveryNoteErr = null;
        },
        fetchDeliveryNotePass: (state: IRentalPackagesState, action: PayloadAction<IDeliveryNote>) => {
            state.fetchingDeliveryNote = false;
            state.deliveryNote = action.payload;
        },
        fetchDeliveryNoteFail: (state: IRentalPackagesState, action: PayloadAction<string>) => {
            state.fetchingDeliveryNote = false;
            state.fetchDeliveryNoteErr = action.payload;
        },
        setDeliveryNote: (state: IRentalPackagesState, action: PayloadAction<IDeliveryNote | null>) => {
            state.deliveryNote = action.payload;
        },
        resetExtendedTimeFrame: (state: IRentalPackagesState) => {
            state.extendedTimeFrame = false;
        },
        clearExtendTimeFrameFieldErr: (state: IRentalPackagesState, action: PayloadAction<string>) => {
            state.extendTimeFrameErrs[action.payload as keyof ExtendTimeFrameErrs] = null;
        },
        clearExtendTimeFrameErrs: (state: IRentalPackagesState) => {
            state.extendTimeFrameErrs = new ExtendTimeFrameErrs();
        },
        tryResetExtendTimeFrameFormErr: (state: IRentalPackagesState) => {
            let shouldReset = true;

            for (let key in state.extendTimeFrameErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.extendTimeFrameErrs[key as keyof ExtendTimeFrameErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.extendTimeFrameErrs.form = null;
            }
        },
        extendTimeFrameStart: (state: IRentalPackagesState) => {
            state.extendingTimeFrame = true;
            state.extendedTimeFrame = false;
        },

        extendTimeFramePass: (state: IRentalPackagesState) => {
            state.extendingTimeFrame = false;
            state.extendedTimeFrame = true;
        },
        extendTimeFrameFail: (state: IRentalPackagesState, action: PayloadAction<IFieldError[]>) => {
            state.extendingTimeFrame = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.extendTimeFrameErrs) {
                    if (err.field === key) {
                        state.extendTimeFrameErrs[key as keyof ExtendTimeFrameErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.extendTimeFrameErrs.form) {
                state.extendTimeFrameErrs.form = 'Form incorrectly filled.';
            }
        },
        clearApproveTimeExtErr: (state: IRentalPackagesState) => {
            state.approveTimeExtErr = null;
        },
        resetTimeExtApproved: (state: IRentalPackagesState) => {
            state.timeExtApproved = false;
        },
        approveTimeExtStart: (state: IRentalPackagesState) => {
            state.approvingTimeExt = true;
            state.approveTimeExtErr = null;
        },
        approveTimeExtPass: (state: IRentalPackagesState) => {
            state.approvingTimeExt = false;
            state.timeExtApproved = true;
        },
        approveTimeExtFail: (state: IRentalPackagesState, action: PayloadAction<string>) => {
            state.approvingTimeExt = false;
            state.approveTimeExtErr = action.payload;
        },
    }
});

export const {
    clearConfirmDeliveryFieldErr,
    clearConfirmDeliveryErrs,
    tryResetConfirmDeliveryFormErr,
    resetDeliveryConfirmed,
    confirmDeliveryStart,
    confirmDeliveryPass,
    confirmDeliveryFail,
    clearFetchDeliveryNoteErr,
    fetchDeliveryNoteStart,
    fetchDeliveryNotePass,
    fetchDeliveryNoteFail,
    setDeliveryNote,
    resetExtendedTimeFrame,
    tryResetExtendTimeFrameFormErr,
    clearExtendTimeFrameFieldErr,
    clearExtendTimeFrameErrs,
    extendTimeFrameStart,
    extendTimeFramePass,
    extendTimeFrameFail,
    clearApproveTimeExtErr,
    resetTimeExtApproved,
    approveTimeExtStart,
    approveTimeExtPass,
    approveTimeExtFail
} = rentalPackagesSlice.actions;

interface ITryConfirmDeliveryArg {
    authToken: string,
    data: {
        orderId: string,
        customerSign: string,
        customerComment?: string,
        remarks?: string;
        driverSign: string
    }
}


export const tryConfirmDelivery = createAsyncThunk('rentalPackages/tryConfirmDelivery', (arg: ITryConfirmDeliveryArg, { dispatch }) => {

    const { authToken, data } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/delivery-note`;

    dispatch(confirmDeliveryStart());

    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(confirmDeliveryPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(confirmDeliveryFail(fieldErrors));
    })
});

interface ITryFetchDeliveryNoteArg {
    authToken: string;
    orderId: string;
}

export const tryFetchDeliveryNote = createAsyncThunk('rentalPackages/tryFetchDeliveryNote', (arg: ITryFetchDeliveryNoteArg, { dispatch }) => {
    const { authToken, orderId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/orders/${orderId}/delivery-note`;

    dispatch(fetchDeliveryNoteStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchDeliveryNotePass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        if (data?.err) {
            dispatch(fetchDeliveryNoteFail(data.err));
        } else {
            dispatch(fetchDeliveryNoteFail('Check your network connection.'));
        }
    })
});

interface ITryExtendTimeFrameArg {
    authToken: string;
    data: {
        rentalPackageId: string;
        daysCount: string;
        paymentTypes: string[];
        bankName: string;
        accountName: string;
        accountNumber: string;
    }

}

export const tryExtendTimeFrame = createAsyncThunk('rentalPackages/tryExtendTimeFrame', (arg: ITryExtendTimeFrameArg, { dispatch }) => {
    const { authToken, data } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-time-frame-extension`;

    dispatch(extendTimeFrameStart());
    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(extendTimeFramePass());
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(extendTimeFrameFail(fieldErrors));
    })

});

interface ITryApproveTimeExtArg {
    authToken: string;
    timeExtId: string;
}

export const tryApproveTimeExt = createAsyncThunk('rentalPackages/tryApproveTimeExt', (arg: ITryApproveTimeExtArg, { dispatch }) => {

    const { authToken, timeExtId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-time-frame-extensions/${timeExtId}/approve`;

    dispatch(approveTimeExtStart());
    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(approveTimeExtPass());
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }

        if (data?.errors) {
            dispatch(approveTimeExtFail(getFieldErrors(data.errors)[0].msg))
        } else if (data?.err) {
            dispatch(approveTimeExtFail(data.err));
        } else {
            dispatch(approveTimeExtFail('Check your network connection.'));
        }
    });
});

export default rentalPackagesSlice.reducer;